import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, MoreVertical, ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { IconButton } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { PrazoCritico } from '@/hooks/useDashboard';

interface Props {
  items: PrazoCritico[];
  loading?: boolean;
}

function formatBRL(value?: number): string {
  if (!value) return '—';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(value);
}

function formatDateTime(d: Date): string {
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const tomorrow = new Date(now); tomorrow.setDate(tomorrow.getDate() + 1);
  const isTomorrow = d.toDateString() === tomorrow.toDateString();
  const hh = d.getHours().toString().padStart(2, '0');
  const mm = d.getMinutes().toString().padStart(2, '0');
  if (sameDay) return `Hoje · ${hh}:${mm}`;
  if (isTomorrow) return `Amanhã · ${hh}:${mm}`;
  return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')} · ${hh}:${mm}`;
}

function isUrgent(d: Date): boolean {
  return (d.getTime() - Date.now()) <= 24 * 3600 * 1000;
}

export function PrazosCriticosCard({ items, loading }: Props) {
  const navigate = useNavigate();

  return (
    <section
      aria-labelledby="prazos-titulo"
      className="bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded-card shadow-sm-warm dark:shadow-sm-dark overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-start justify-between px-[18px] pt-4 pb-3">
        <div>
          <h3 id="prazos-titulo" className="text-[14px] font-semibold text-ink-title dark:text-ink-title-dark tracking-[-0.005em]">
            Próximos prazos críticos
          </h3>
          <p className="mt-0.5 text-[12px] text-ink-subtitle dark:text-ink-subtitle-dark">
            Editais cuja submissão ou disputa ocorre nos próximos 7 dias
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/tenders')}
          className="inline-flex items-center gap-1 text-[12px] font-medium text-brand-700 dark:text-brand-300 hover:text-brand-800 dark:hover:text-brand-200 transition-colors"
        >
          Ver todos <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Body */}
      <div className="border-t border-line dark:border-line-dark">
        {loading ? (
          <>
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="grid grid-cols-[140px_1fr_140px_120px_28px] gap-3 items-center py-3 px-[18px] border-t border-line dark:border-line-dark first:border-t-0">
                <div><Skeleton className="h-3 w-24" /><Skeleton className="mt-1 h-2.5 w-32" /></div>
                <Skeleton className="h-4 w-full max-w-xs" />
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-3 w-20 ml-auto" />
                <Skeleton className="h-6 w-6" circle />
              </div>
            ))}
          </>
        ) : items.length === 0 ? (
          <div className="py-10 text-center text-[13px] text-ink-subtitle dark:text-ink-subtitle-dark">
            Nenhum prazo crítico nos próximos 7 dias.
          </div>
        ) : (
          items.map((p, i) => {
            const urgent = isUrgent(p.dataLimite);
            return (
              <div
                key={p.id}
                className={[
                  'group w-full grid grid-cols-[1fr_28px] gap-3 items-center pr-[18px]',
                  'text-left hover:bg-surface-alt dark:hover:bg-surface-dark-alt transition-colors',
                  i > 0 ? 'border-t border-line dark:border-line-dark' : '',
                ].join(' ')}
              >
                <button
                  type="button"
                  onClick={() => p.tipo === 'tender' && p.targetId != null ? navigate(`/tenders`) : undefined}
                  className="grid grid-cols-[140px_1fr_140px_120px] gap-3 items-center py-3 pl-[18px] text-left min-w-0"
                >
                <div className="min-w-0">
                  <div className="text-[12.5px] font-semibold text-ink-title dark:text-ink-title-dark tabular truncate">
                    {p.titulo.split(' - ')[0] || p.titulo}
                  </div>
                  <div className="text-[11px] text-ink-subtitle dark:text-ink-subtitle-dark truncate">
                    {p.subtitulo}
                  </div>
                </div>
                <div className="text-[13px] text-ink-label dark:text-ink-label-dark truncate">
                  {p.titulo.includes(' - ') ? p.titulo.split(' - ').slice(1).join(' - ') : p.titulo}
                </div>
                <div className={[
                  'inline-flex items-center gap-1.5 text-[12.5px] tabular',
                  urgent ? 'text-warning dark:text-warning-dark font-semibold' : 'text-ink-text dark:text-ink-text-dark',
                ].join(' ')}>
                  <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>{formatDateTime(p.dataLimite)}</span>
                </div>
                <div className="text-[12.5px] tabular text-right text-ink-title dark:text-ink-title-dark font-medium">
                  {formatBRL(p.valor)}
                </div>
                </button>
                <IconButton
                  onClick={() => {}}
                  aria-label="Mais ações"
                  className="!w-7 !h-7"
                >
                  <MoreVertical className="w-3.5 h-3.5" />
                </IconButton>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}
